import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { UpdatePriorityAPI } from '../../projects/api/ProjectService';
import {
  AddLocationAPI,
  DeleteLocationAPI,
  DuplicateLocationAPI,
  FetchLocationAPI,
  UpdateLocationAPI,
} from '../api/LocationService';

/**
 * Fetch Locations
 * @param {*} requestParameters
 * @param {Number} requestParameters.id - Project id to fetch locations for
 */
export const fetchLocations = createAsyncThunk(
  'locations/fetchLocations',
  async (data, { rejectWithValue }) => {
    const apiResponse = await FetchLocationAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const areaSchema = new schema.Entity('areas');
      const zoneSchema = new schema.Entity('zones', {
        areas: [areaSchema],
      });
      const locationSchema = new schema.Entity('locations', {
        zones: [zoneSchema],
      });

      const formattedData = new schema.Array(locationSchema);

      const formattedResponse = normalize(apiResponse.response.data, formattedData);
      return { ...formattedResponse, rawData: apiResponse?.response?.data };
    }
  }
);

/** Create Location
 * @param {*} requestParameters
 * @param {String} requestParameters.name
 * @param {Number} requestParameters.project_id
 */
export const createLocation = createAsyncThunk(
  'locations/createLocation',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await AddLocationAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Update Location
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 * @param {Name} requestParameters.name
 */
export const updateLocation = createAsyncThunk(
  'locations/updateLocation',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await UpdateLocationAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Delete Location
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 */
export const deleteLocation = createAsyncThunk(
  'locations/deleteLocation',
  async (requestParameters, { rejectWithValue, getState }) => {
    const apiResponse = await DeleteLocationAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const locationsState = getState().locations;
      const zonesState = getState().zones;
      const location = requestParameters.id;
      const zones = locationsState.entities[location].zones
        ? locationsState.entities[location].zones
        : [];
      const areas = zones.reduce(
        (areas, zoneId) => areas.concat(zonesState.entities[zoneId].areas),
        []
      );

      return { location, zones, areas };
    }
  }
);

/** Duplicate location
 * @param {*} requestParameters
 * @param {Number} requestParameters.id - Location to duplicate
 */
export const duplicateLocation = createAsyncThunk(
  'locations/duplicateLocations',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await DuplicateLocationAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const areaSchema = new schema.Entity('areas');
      const zoneSchema = new schema.Entity('zones', {
        areas: [areaSchema],
      });
      const locationSchema = new schema.Entity('locations', {
        zones: [zoneSchema],
      });

      const formattedData = new schema.Array(locationSchema);

      const formattedResponse = normalize([apiResponse.response.data], formattedData);
      return formattedResponse;
    }
  }
);

/** Reorder Locations
 * @param {*} requestParameters
 * @param {Number} requestParameters.sourceIndex
 * @param {Number} requestParameters.destinationIndex
 * @param {Number} requestParameters.parentId
 */
export const reorderLocations = createAsyncThunk(
  'locations/reorderLocations',
  async (requestParameters, { rejectWithValue, getState, dispatch }) => {
    const parentId = requestParameters.parentId;
    const locationIds = [...getState().locations.ids];
    let destinationIndex = requestParameters.destinationIndex;
    let sourceIndex = requestParameters.sourceIndex;
    const id = locationIds.splice(sourceIndex, 1);
    locationIds.splice(destinationIndex, 0, ...id);
    dispatch(setLocationOrder({ locations: locationIds }));
    const apiResponse = await UpdatePriorityAPI({
      orderingType: 'location',
      locationIds,
      parentId: parentId,
    });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

export const locationsAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.priority - b.priority,
});

const initialState = locationsAdapter.getInitialState({
  error: null,
});

export const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    clearLocations: locationsAdapter.removeAll,
    addEmptyLocation: (state, action) => {
      locationsAdapter.addOne(state, {
        id: action.payload.id,
        project_id: action.payload.projectId,
        name: 'New Location',
      });
    },
    removeEmptyLocation: (state, action) => {
      locationsAdapter.removeOne(state, action.payload.id);
    },
    setLocationZoneOrder: (state, action) => {
      state.entities[action.payload.locationId].zones = action.payload.zones;
    },
    setLocationOrder: (state, action) => {
      state.ids = action.payload.locations;
    },
    updateLocationSelection: (state, action) => {
      locationsAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    //Fetch
    builder.addCase(fetchLocations.fulfilled, (state, action) => {
      if (action.payload.entities.locations) {
        locationsAdapter.setAll(state, action.payload.entities.locations);
      }
    });
    builder.addCase(fetchLocations.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Create
    builder.addCase(createLocation.fulfilled, (state, action) => {
      // Remove placeholder location
      locationsAdapter.removeOne(state, action.meta.arg.id);
      // Add new location
      locationsAdapter.addOne(state, { ...action.payload, zones: [] });
    });
    builder.addCase(createLocation.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Update
    builder.addCase(updateLocation.fulfilled, (state, action) => {
      locationsAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(updateLocation.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Delete
    builder.addCase(deleteLocation.fulfilled, (state, action) => {
      locationsAdapter.removeOne(state, action.payload.location);
    });
    builder.addCase(deleteLocation.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase('zones/deleteZone/fulfilled', (state, action) => {
      state.entities[action.payload.location].zones = state.entities[
        action.payload.location
      ].zones.filter((zoneId) => zoneId !== action.payload.zone);
    });

    builder.addCase('zones/removeEmptyZone', (state, action) => {
      state.entities[action.payload.locationId].zones = state.entities[
        action.payload.locationId
      ].zones.filter((zoneId) => zoneId !== action.payload.id);
    });

    //Duplicate
    builder.addCase(duplicateLocation.fulfilled, (state, action) => {
      locationsAdapter.upsertMany(state, action.payload.entities.locations);
    });
    builder.addCase(duplicateLocation.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase(reorderLocations.fulfilled, (state, action) => {
      locationsAdapter.upsertMany(state, action.payload);
    });

    //Add Empty Zone
    builder.addCase('zones/addEmptyZone', (state, action) => {
      const locationId = action.payload?.locationId;
      const zones = state.entities[locationId]?.zones;
      if (zones?.length) {
        zones?.push(action.payload.id);
      } else {
        state.entities[locationId].zones = [action.payload.id];
      }
    });

    //Create zone
    builder.addCase('zones/createZone/fulfilled', (state, action) => {
      state.entities[action.payload.location_id].zones = state.entities[
        action.payload.location_id
      ].zones.filter((zoneId) => zoneId !== action.meta.arg.id);
      state.entities[action.payload.location_id].zones.splice(
        action.payload.priority - 1,
        0,
        action.payload.id
      );
    });

    builder.addCase('zones/duplicateZone/fulfilled', (state, action) => {
      if (action?.meta?.arg && action.payload?.result?.length) {
        state.entities[action.meta.arg.locationId].zones.splice(
          action.meta.arg.priority + 1,
          0,
          action.payload.result[0]
        );
      }
    });
  },
});

export default locationsSlice.reducer;

export const {
  clearLocations,
  addEmptyLocation,
  setLocationZoneOrder,
  setLocationOrder,
  removeEmptyLocation,
  updateLocationSelection,
} = locationsSlice.actions;

/** Selectors **/
export const {
  selectById: selectLocationById,
  selectIds: selectLocationIds,
  selectEntities: selectLocationEntities,
  selectAll: selectAllLocations,
  selectTotal: selectTotalLocations,
} = locationsAdapter.getSelectors((state) => state.locations);

export const selectAllLocationsByProjectId = createSelector(
  [(state) => selectAllLocations(state), (state, projectId) => projectId],
  (allLocations, projectId) => {
    return projectId
      ? allLocations.filter((location) => location?.project_id == projectId)
      : allLocations;
  }
);
